import { type EnvironmentId, type ThreadId } from "@cmmd-forge/contracts";
import { Cause } from "effect";

import { sanitizePublicUiError } from "../../productSafeLabels";
import { useStore } from "../../store";

/**
 * The only honest default when a thread stream dies without saying why.
 *
 * It is a FALLBACK, not a description: it was previously applied to every
 * failure class, so on 2026-08-26 a runtime whose disk had filled reported that
 * the user no longer had access to their own thread. `onError` is handed the
 * Effect cause by managedRpcSession, so the real reason is available — it was
 * simply being discarded.
 */
export const THREAD_SUBSCRIPTION_FALLBACK_ERROR = "Thread not found or you no longer have access.";

/**
 * Turn a subscription failure into product copy.
 *
 * Falls back rather than guessing: an absent, empty, or path-bearing cause keeps
 * the neutral message, because a wrong specific reason is worse than a vague
 * one. `sanitizePublicUiError` already drops anything carrying an internal path.
 */
export function describeThreadSubscriptionFailure(error: unknown): string {
  if (error === undefined || error === null) return THREAD_SUBSCRIPTION_FALLBACK_ERROR;
  // managedRpcSession (managedRpcSession.ts:97) hands us the Effect CAUSE, not the
  // error. Squash it first: `String(cause)` renders
  // "Cause([Fail(Error: No space left on device)])" — the reason is in there, so a
  // naive `toContain` assertion passes while the user reads Effect internals.
  // Squashing yields the underlying Error, whose `.message` is what
  // sanitizePublicUiError actually wants.
  const failure = Cause.isCause(error) ? Cause.squash(error) : error;
  const described = sanitizePublicUiError(failure, THREAD_SUBSCRIPTION_FALLBACK_ERROR);
  // A genuine miss is the one case the fallback describes accurately, and the
  // server states it as `Thread <uuid> was not found` (ws.ts). Surfacing that
  // verbatim would put a raw thread id in product copy and say nothing the user
  // can act on, so the friendly wording stays for this class only.
  return /\bnot found\b/i.test(described) ? THREAD_SUBSCRIPTION_FALLBACK_ERROR : described;
}

/**
 * Record a thread-stream failure as product copy on the store.
 *
 * Lives here rather than inline at the subscription so the whole
 * "what does the user get told when a thread stream dies" decision is one
 * module — the previous split is how a hardcoded access message survived for
 * every failure class.
 */
export function recordThreadSubscriptionFailure(
  environmentId: EnvironmentId,
  threadId: ThreadId,
  error: unknown,
): void {
  useStore
    .getState()
    .setThreadSubscriptionError(environmentId, threadId, describeThreadSubscriptionFailure(error));
}
